import JSZip from 'jszip';
import { AgentMarketPlaceItemWithFilter } from '../pages/userAgent/agentMarketPlace/AgentMarketPlace';
import { filterAgentConfigFields } from './utils/agentUtils';

export async function generateAgentPackage(agentData: Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string }>): Promise<Blob | null> {
  if (!agentData.name) {
    console.error('Please provide a name for the agent before generating package');
    return null;
  }

  const zip = new JSZip();
  // Use folder_name field if present, else fallback to name
  const folderNameRaw = agentData.folder_name || agentData.name || '';
  const folderName = folderNameRaw.replace(/[^a-zA-Z0-9-_]/g, '_').toLowerCase();

  // Use strict filtering for config.json
  const filtered = filterAgentConfigFields(agentData);
  
  // Ensure tech_stack is always an array
  let techStackArray: string[] = [];
  if (Array.isArray(filtered.tech_stack)) {
    techStackArray = filtered.tech_stack;
  } else if (typeof filtered.tech_stack === 'string') {
    techStackArray = (filtered.tech_stack as string).split(',').map((t: string) => t.trim()).filter(Boolean);
  }
  
  const configData: any = {
    party_id: filtered.party_id,
    name: filtered.name,
    folder_name: filtered.folder_name,
    party_type: filtered.party_type,
    description: filtered.description,
    service_name: filtered.service_name,
    port: filtered.port,
    allow_all_access: true,
    owners: filtered.owners,
    last_updated: filtered.last_updated,
    icon: filtered.icon,
    category: filtered.category,
    role: filtered.role,
    isPlatformAgent: filtered.isPlatformAgent,
    datasetIds: filtered.datasetIds !== undefined ? filtered.datasetIds : null,
    supports_file_upload: filtered.supports_file_upload,
    file_format_supported: filtered.file_format_supported !== undefined ? filtered.file_format_supported : [],
    prompt1: filtered.prompt1,
    prompt2: filtered.prompt2,
    prompt3: filtered.prompt3,
    tech_stack: techStackArray,
    cloud_provider: filtered.cloud_provider,
    custom_ui: filtered.custom_ui !== undefined ? filtered.custom_ui : null,
    supports_dataset: filtered.supports_dataset,
    ui_url: filtered.ui_url !== undefined ? filtered.ui_url : null,
    download_url: filtered.download_url !== undefined ? filtered.download_url : null,
    demo_video_url: filtered.demo_video_url,
    downloadable_only: filtered.downloadable_only,
  };

  // Only add agents field if party_type is 'Team'
  if (filtered.party_type === 'Team' && (filtered as any).agents !== undefined) {
    configData.agents = (filtered as any).agents;
  }

  // Create README.md content
  // If detailed_description is provided, use it; otherwise generate default template
  let readmeContent: string;
  
  if (filtered.detailed_description && filtered.detailed_description.trim() !== '') {
    // Use the user-provided markdown content
    readmeContent = filtered.detailed_description;
  } else {
    // Fallback to auto-generated template if no detailed_description is provided
    readmeContent = `# ${filtered.name || 'Untitled Agent'}

## Description
${filtered.description || 'No description provided'}

## Category
${filtered.category || 'Not specified'}

## Role
${filtered.role || 'Not specified'}

## Tech Stack
${techStackArray.length > 0 ? techStackArray.join(', ') : 'Not specified'}

## Cloud Provider
${filtered.cloud_provider || 'Not specified'}

## Configuration Details
- **Service Name**: ${filtered.service_name || 'Not specified'}
- **Port**: ${filtered.port || 8000}
- **Supports File Upload**: ${filtered.supports_file_upload ? 'Yes' : 'No'}
- **File Formats Supported**: ${filtered.file_format_supported ? filtered.file_format_supported.join(', ') : 'None'}
- **Is Platform Agent**: ${filtered.isPlatformAgent ? 'Yes' : 'No'}

## Prompts
### Prompt 1
${filtered.prompt1 || 'null'}

### Prompt 2
${filtered.prompt2 || 'null'}

### Prompt 3
${filtered.prompt3 || 'null'}

## Owners
${filtered.owners && filtered.owners.length > 0 ? filtered.owners.map((owner: any) => `- ${owner.name} (${owner.email})`).join('\n') : 'No owners specified'}

---
*Generated on ${new Date().toLocaleDateString()}*
`;
  }
  // Add files to zip with proper folder structure
  const folder = zip.folder(folderName);
  if (folder) {
    folder.file('config.json', JSON.stringify(configData, null, 2));
    folder.file('README.md', readmeContent);
  } else {
    // Fallback to root level if folder creation fails
    zip.file('config.json', JSON.stringify(configData, null, 2));
    zip.file('README.md', readmeContent);
  }

  try {
    // Generate and return zip file blob with proper compression
    const content = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 9
      }
    });
    return content;
  } catch (error) {
    console.error('Error generating zip file:', error);
    return null;
  }
}
